/**
 * The realtime transport: a thin, typed wrapper over the `phoenix` package.
 *
 * This module deliberately knows nothing about how events are applied. It
 * connects, joins `user:<id>` and whichever `room:<id>` topics are open,
 * validates what arrives and hands it upward. The store decides what any of it
 * means — and, critically, whether the connection is trustworthy yet.
 *
 * The socket is an optimisation, never the source of truth. Everything it
 * delivers is also reachable through `/sync`, so a failure here degrades the
 * client to polite slowness rather than to incorrectness.
 */

import { Socket, type Channel } from 'phoenix'

import { api } from '@/api/client'

import {
  DURABLE_EVENT_TYPES,
  EPHEMERAL_EVENT_TYPES,
  isDurableEvent,
  type Cursor,
  type DurableEvent,
  type EphemeralEvent,
  type ReadSignal,
  type RelaiiEvent,
  type TypingSignal,
  type UserTopicJoinReply,
  type UUID,
} from '@/api/types'

const SOCKET_PATH = '/socket/v1'
const EVENT_MESSAGE = 'event'
const JOIN_TIMEOUT_MS = 15_000
const TOKEN_RETRY_MS = 5_000

interface SocketTokenResponse {
  token: string
  expires_in: number
}
const HEARTBEAT_MS = 25_000

/** Reconnect backoff ceiling. Long enough to be polite, short enough to feel alive. */
const RECONNECT_CAP_MS = 30_000

export type ConnectionStatus = 'connecting' | 'online' | 'offline' | 'reconnecting'

/** Which topic an event arrived on. Only the user topic is a complete stream. */
export type EventOrigin = 'user' | 'room'

export interface SocketHandlers {
  onDurable(event: DurableEvent, origin: EventOrigin): void
  onEphemeral(event: EphemeralEvent): void
  onStatus(status: ConnectionStatus, error: string | null): void
  /**
   * Fired on every successful join of `user:<id>`, including automatic rejoins.
   * `missed > 0`, or any rejoin at all, means the caller must run `/sync`
   * before trusting anything else this socket delivers.
   */
  onUserJoin(reply: UserTopicJoinReply, rejoin: boolean): void
}

export interface RelaiiSocketOptions {
  userId: UUID
  /** Read at join time so a rejoin always carries the current position. */
  cursor: () => Cursor
  /**
   * Bearer token for hosts that hold one. The web client authenticates with the
   * same HttpOnly cookie the browser attaches to the handshake, and has no
   * token to give.
   */
  token?: () => string | null
  /** Overrides the socket mount point. Defaults to `/socket/v1`. */
  path?: string
}

// ── Backoff ─────────────────────────────────────────────────────────────────

const DURABLE_TYPES: ReadonlySet<string> = new Set<string>(DURABLE_EVENT_TYPES)
const EPHEMERAL_TYPES: ReadonlySet<string> = new Set<string>(EPHEMERAL_EVENT_TYPES)

/**
 * Exponential backoff with full jitter, capped.
 *
 * The jitter is not decoration: a self-hosted relaii restarting drops every
 * client at once, and without it they would all return in lockstep and knock
 * the server over again on each retry.
 */
export function reconnectDelay(tries: number): number {
  const step = Math.min(Math.max(tries, 1), 8)
  const ceiling = Math.min(RECONNECT_CAP_MS, 1_000 * 2 ** (step - 1))
  return Math.round(ceiling / 2 + Math.random() * (ceiling / 2))
}

// ── Wire validation ─────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Narrows a socket payload to an Event, or returns null.
 *
 * A newer server may emit types this build has never heard of; dropping them
 * is correct, and far better than letting a half-shaped object into the store.
 */
export function parseEvent(input: unknown): RelaiiEvent | null {
  if (!isRecord(input)) return null

  const type = input['type']
  if (typeof type !== 'string') return null
  if (!isRecord(input['payload'])) return null

  if (DURABLE_TYPES.has(type)) {
    if (typeof input['cursor'] !== 'number') return null
    if (typeof input['room_id'] !== 'string') return null
    return input as unknown as DurableEvent
  }

  if (EPHEMERAL_TYPES.has(type)) return input as unknown as EphemeralEvent

  return null
}

function parseJoinReply(input: unknown, fallbackCursor: Cursor): UserTopicJoinReply {
  if (!isRecord(input)) return { cursor: fallbackCursor, missed: 0 }

  const cursor = input['cursor']
  const missed = input['missed']

  return {
    cursor: typeof cursor === 'number' ? cursor : fallbackCursor,
    // An unreadable reply is treated as "something was missed": resyncing
    // costs one cheap request, while wrongly trusting the socket loses events.
    missed: typeof missed === 'number' ? missed : 1,
  }
}

function describe(reason: unknown): string | null {
  if (reason === undefined || reason === null) return null
  if (typeof reason === 'string') return reason
  if (reason instanceof Error) return reason.message
  if (isRecord(reason)) {
    const message = reason['reason'] ?? reason['message']
    if (typeof message === 'string') return message
  }
  return null
}

// ── The socket ──────────────────────────────────────────────────────────────

export class RelaiiSocket {
  private socket: Socket | null = null
  // The browser's access token is in an HttpOnly cookie and a WebSocket URL is
  // the only place a browser can carry a credential into the handshake, so the
  // client asks the server for a short-lived, socket-only token and keeps it
  // here. Phoenix calls `params` synchronously on every reconnect, so it has to
  // already be in hand — hence the cache and the refresh timer rather than a
  // fetch at connect time.
  private socketToken: string | null = null
  private tokenTimer: ReturnType<typeof setTimeout> | null = null
  private userChannel: Channel | null = null
  private readonly roomChannels = new Map<UUID, Channel>()
  private readonly openRooms = new Set<UUID>()

  /**
   * Mutated in place before every join. Phoenix keeps a reference to this
   * object and reads it when it rejoins on its own, so updating the field is
   * what makes an automatic rejoin carry a current cursor rather than the one
   * the tab started with.
   */
  private readonly joinParams: { since: Cursor } = { since: 0 }

  private joinCount = 0
  private status: ConnectionStatus = 'offline'
  private closing = false

  constructor(
    private readonly options: RelaiiSocketOptions,
    private readonly handlers: SocketHandlers,
  ) {}

  get connectionStatus(): ConnectionStatus {
    return this.status
  }

  connect(): void {
    if (this.socket !== null) return

    this.closing = false
    this.joinCount = 0

    // A native host holds its own bearer token and can open immediately. A
    // browser cannot: the handshake carries its credential in the URL, so the
    // token has to be in hand *before* the socket is constructed, not fetched
    // alongside it. Phoenix builds the URL synchronously and would otherwise
    // hand the server a tokenless handshake and be refused.
    if (this.options.token === undefined && this.socketToken === null) {
      this.setStatus('connecting', null)
      // `refreshSocketToken` opens the socket once a token is in hand, and
      // keeps asking until it is. Opening without one would only hand the
      // server a handshake it refuses, and Phoenix would retry that refusal
      // on a loop of its own.
      void this.refreshSocketToken()
      return
    }

    this.openSocket()
  }

  private openSocket(): void {
    if (this.socket !== null) return

    const socket = new Socket(this.options.path ?? SOCKET_PATH, {
      // A function, so a token rotated during the session is picked up by the
      // reconnect rather than replaying an expired one forever.
      params: () => {
        // A native host supplies its own bearer token; a browser uses the
        // short-lived socket token fetched above.
        const token = this.options.token?.() ?? this.socketToken
        return token === null ? {} : { token }
      },
      heartbeatIntervalMs: HEARTBEAT_MS,
      reconnectAfterMs: reconnectDelay,
      rejoinAfterMs: reconnectDelay,
    })

    socket.onOpen(() => {
      // The transport is up but nothing has been replayed yet; the caller only
      // learns it is safe to trust live events once the user topic has joined.
      this.joinParams.since = this.options.cursor()
      this.setStatus('connecting', null)
    })

    socket.onError((error: unknown) => {
      this.setStatus(this.closing ? 'offline' : 'reconnecting', describe(error))
    })

    socket.onClose(() => {
      if (this.closing) this.setStatus('offline', null)
      else this.setStatus('reconnecting', null)
    })

    this.socket = socket
    this.setStatus('connecting', null)
    socket.connect()

    this.joinUserTopic(socket)
    this.reconcileRooms()
  }

  disconnect(): void {
    this.closing = true
    this.clearTokenRefresh()

    for (const [roomId, channel] of this.roomChannels) {
      this.roomChannels.delete(roomId)
      safely(() => channel.leave())
    }

    const user = this.userChannel
    this.userChannel = null
    if (user !== null) safely(() => user.leave())

    const socket = this.socket
    this.socket = null
    if (socket !== null) safely(() => socket.disconnect())

    this.setStatus('offline', null)
  }

  /** Joins `room:<id>`, or remembers to once the socket is up. */
  openRoom(roomId: UUID): void {
    if (this.openRooms.has(roomId)) return
    this.openRooms.add(roomId)
    this.reconcileRooms()
  }

  closeRoom(roomId: UUID): void {
    if (!this.openRooms.delete(roomId)) return
    this.reconcileRooms()
  }

  /** Best effort, exactly as the contract describes it. Never throws. */
  sendTyping(roomId: UUID, typing: boolean): void {
    const signal: TypingSignal = { typing }
    this.push(roomId, 'typing', signal)
  }

  /** Best effort. Read state is also written over HTTP, which is authoritative. */
  sendRead(roomId: UUID, sequence: number): void {
    const signal: ReadSignal = { sequence }
    this.push(roomId, 'read', signal)
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private push(roomId: UUID, event: string, payload: object): void {
    const channel = this.roomChannels.get(roomId)
    if (channel === undefined) return

    // Phoenix throws when a channel is pushed before it has joined, which is a
    // normal race while reconnecting rather than something to surface.
    safely(() => {
      channel.push(event, payload)
    })
  }

  /**
   * Fetches a socket token and schedules the next fetch at half its lifetime,
   * so a reconnect at any moment finds one that has not expired.
   *
   * A failure is not fatal: the socket is an optimisation, and /sync still
   * carries every durable event. It retries, and the UI stays on cached data
   * meanwhile rather than showing an error for something the user cannot act on.
   */
  private async refreshSocketToken(): Promise<void> {
    if (this.closing) return

    try {
      const { token, expires_in } = await api.post<SocketTokenResponse>('/auth/socket-token')
      if (this.closing) return
      this.socketToken = token
      this.scheduleTokenRefresh(Math.max(15, Math.floor(expires_in / 2)) * 1000)
      // The first token, or the first one after a run of failures: the
      // socket has been waiting for it.
      if (this.options.token === undefined && this.socket === null) this.openSocket()
    } catch {
      this.scheduleTokenRefresh(TOKEN_RETRY_MS)
    }
  }

  private scheduleTokenRefresh(delayMs: number): void {
    if (this.tokenTimer !== null) clearTimeout(this.tokenTimer)
    if (this.closing) return
    this.tokenTimer = setTimeout(() => void this.refreshSocketToken(), delayMs)
  }

  private clearTokenRefresh(): void {
    if (this.tokenTimer !== null) {
      clearTimeout(this.tokenTimer)
      this.tokenTimer = null
    }
    this.socketToken = null
  }

  private setStatus(status: ConnectionStatus, error: string | null): void {
    if (this.status === status && error === null) return
    this.status = status
    this.handlers.onStatus(status, error)
  }

  private joinUserTopic(socket: Socket): void {
    this.joinParams.since = this.options.cursor()

    const channel = socket.channel(`user:${this.options.userId}`, this.joinParams)
    channel.on(EVENT_MESSAGE, (payload: unknown) => this.dispatch(payload, 'user'))
    this.userChannel = channel

    // Phoenix replays these hooks on every rejoin, which is precisely the
    // signal the store needs: a rejoin means time passed with the socket down.
    channel
      .join(JOIN_TIMEOUT_MS)
      .receive('ok', (reply: unknown) => {
        this.joinCount += 1
        const parsed = parseJoinReply(reply, this.joinParams.since)
        this.setStatus('online', null)
        this.handlers.onUserJoin(parsed, this.joinCount > 1)
      })
      .receive('error', (reason: unknown) => {
        this.setStatus('reconnecting', describe(reason))
      })
      .receive('timeout', () => {
        this.setStatus('reconnecting', 'The server did not answer in time.')
      })
  }

  private reconcileRooms(): void {
    const socket = this.socket
    if (socket === null) return

    for (const [roomId, channel] of this.roomChannels) {
      if (this.openRooms.has(roomId)) continue
      this.roomChannels.delete(roomId)
      safely(() => channel.leave())
    }

    for (const roomId of this.openRooms) {
      if (this.roomChannels.has(roomId)) continue

      const channel = socket.channel(`room:${roomId}`)
      channel.on(EVENT_MESSAGE, (payload: unknown) => this.dispatch(payload, 'room'))
      this.roomChannels.set(roomId, channel)
      safely(() => {
        channel.join(JOIN_TIMEOUT_MS)
      })
    }
  }

  private dispatch(payload: unknown, origin: EventOrigin): void {
    const event = parseEvent(payload)
    if (event === null) return

    if (isDurableEvent(event)) this.handlers.onDurable(event, origin)
    else this.handlers.onEphemeral(event)
  }
}

function safely(action: () => void): void {
  try {
    action()
  } catch {
    // Phoenix throws on a few ordering races (pushing before a join completes,
    // leaving a channel whose socket has already gone). None of them are
    // actionable, and none should reach the interface.
  }
}
